import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import Modal, { ModalActions } from '../Modal/Modal';
import Button from '../Button/Button';
import { useRescueContext } from '../../lib/RescueContext';
import { rescueDocumentOverviewQuery, RescueDocumentOverviewData } from '../../lib/documentQueries';
import { SUPPORT_EMAIL } from '../../lib/emails';
import heroSrc from '../../assets/documents-hero.jpg';
import sharedStyles from '../EnrollPictureScreen/EnrollPictureScreen.module.scss';
import styles from './DocumentsReminder.module.scss';
import {
  clearReminderLogin,
  missingDocumentCopy,
  missingDocuments,
  orderedDevices,
  recordReminderView,
  reminderDue,
} from './reminderRules';
import { PdfIcon } from '../Documents/DocumentIcons';
import { AppState } from '../../../lib/store';
import { ResellerTaxExemptType } from '@/types/gql-op-types';

const ACCOUNT_PATH = '/rescue/account';

export default function DocumentsReminder() {
  const history = useHistory();
  const location = useLocation();
  const { taxExemptType } = useRescueContext();
  const [open, setOpen] = useState(false);

  const userId = useSelector((state: AppState) => state.session?.userId ?? null);
  const { data: overview } = useQuery<RescueDocumentOverviewData>(rescueDocumentOverviewQuery);
  const missing = missingDocuments(overview?.rescueDocumentOverview);
  const government = taxExemptType === ResellerTaxExemptType.GOVERNMENT_ENTITY;
  const devices = overview ? orderedDevices(overview.rescueDocumentOverview) : '';

  // Already on the page it points to, so it would only be in the way.
  const onAccountPage = location.pathname.startsWith(ACCOUNT_PATH);
  const due = !!userId && missing.length > 0 && !onAccountPage && reminderDue(userId);

  useEffect(() => {
    if (due && userId) {
      recordReminderView(userId);
      setOpen(true);
    }
  }, [due, userId]);

  // Nothing missing answers this login, so the reminder can't surface later in the session.
  useEffect(() => {
    if (overview && missing.length === 0) {
      clearReminderLogin();
    }
  }, [overview, missing.length]);

  const goUpload = () => {
    setOpen(false);
    history.push(ACCOUNT_PATH);
  };

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      size="extra-wide"
      className={sharedStyles.modal}
      backdropClassName={sharedStyles.backdrop}
      showCloseX={false}
    >
      <img className={`${sharedStyles.hero} ${styles.hero}`} src={heroSrc} alt="" />
      <button
        type="button"
        className={`${sharedStyles.closeX} ${sharedStyles.closeXLight}`}
        onClick={() => setOpen(false)}
        aria-label="Close"
      >
        &times;
      </button>

      <div className={sharedStyles.body}>
        <h3 className={sharedStyles.title}>Keep your free Fi orders coming</h3>
        <p className={sharedStyles.description}>
          {devices} ship tax-free to {government ? 'government shelters' : 'nonprofit rescues'}, so we need a few
          documents on file before your next order. Add them on your account page.
        </p>
        <div className={styles.docs}>
          {missing.map((entry) => {
            const copy = missingDocumentCopy(entry, government, devices);
            return (
              <div className={styles.doc} key={`${entry.documentType}:${entry.state ?? ''}`}>
                <span className={styles.docIcon}>
                  <PdfIcon />
                </span>
                <span className={styles.docMeta}>
                  <span className={styles.docName}>{copy.name}</span>
                  <span className={styles.docFor}>{copy.neededFor}</span>
                  <span className={styles.docDetail}>{copy.detail}</span>
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className={`${sharedStyles.footer} ${styles.footer}`}>
        <ModalActions>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Remind me later
          </Button>
          <Button variant="dark" onClick={goUpload}>
            Upload documents &rarr;
          </Button>
        </ModalActions>
      </div>

      <div className={sharedStyles.support}>
        Have questions?{' '}
        <a href={`mailto:${SUPPORT_EMAIL}?subject=Fi%20Rescue%20Tax%20Documents%20Questions`}>Contact support</a>
      </div>
    </Modal>
  );
}
